import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useAuth } from "/imports/state/AuthContext";
import { PhoneField, isValidPhone, toE164 } from "/imports/ui/components/shared";
import api from "/imports/api/client";

function ClaimEmailView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { completeEmailClaim } = useAuth();

  const [status, setStatus] = useState("verifying");
  const [prefill, setPrefill] = useState(null);
  const [slug, setSlug] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    api
      .claimEmailVerify(token)
      .then((data) => {
        setPrefill(data.client);
        setSlug(data.slug || "");
        setPhoneNumber(data.client?.phoneNumber || "");
        setStatus("form");
      })
      .catch((err) => {
        setStatus("error");
        setError(err.message || "This link is invalid or has expired.");
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError("Please choose a password");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (!phoneNumber.trim()) {
      setError("Please enter your phone number");
      return;
    }
    // The prefill is only a convenience — the club's record may hold a number
    // typed at the desk years ago, and a half-typed one silently costs the client
    // every WhatsApp reminder. What the client confirms here is authoritative, so
    // it is held to the same format whether they retyped it or accepted ours.
    if (!isValidPhone(phoneNumber)) {
      setError("Please enter a valid phone number");
      return;
    }

    setSubmitting(true);
    try {
      const data = await completeEmailClaim(token, password, toE164(phoneNumber));
      const target = data?.slug || slug;
      if (target && data?.preferencesToken) {
        navigate(`/${target}/preferences`, { replace: true, state: { preferencesToken: data.preferencesToken, afterSave: "home" } });
      } else {
        navigate(target ? `/${target}` : "/", { replace: true });
      }
    } catch (err) {
      setError(err.message || "We couldn't complete your signup. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" p={3}>
      <Card sx={{ maxWidth: "28.75rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          {status === "verifying" && (
            <Box textAlign="center">
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" fontWeight={600}>
                Checking your link...
              </Typography>
            </Box>
          )}

          {status === "error" && (
            <Box textAlign="center">
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Link not valid
              </Typography>
              <Alert severity="error" sx={{ mt: 1, textAlign: "left" }}>
                {error}
              </Alert>
              <Typography variant="body2" color="textSecondary" mt={2}>
                Please request a new link from the signup page.
              </Typography>
            </Box>
          )}

          {status === "form" && (
            <>
              <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
                Welcome back{prefill?.firstName ? `, ${prefill.firstName}` : ""}
              </Typography>
              <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
                Set a password to finish claiming your account.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField fullWidth label="First Name" value={prefill?.firstName || ""} disabled margin="normal" />
                <TextField fullWidth label="Last Name" value={prefill?.lastName || ""} disabled margin="normal" />
                <TextField fullWidth label="Email" value={prefill?.email || ""} disabled margin="normal" />
                <PhoneField
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  required
                  margin="normal"
                  helperText="Update this if it's out of date or incorrect"
                />
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  autoComplete="new-password"
                  helperText="At least 6 characters"
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((s) => !s)}
                          onMouseDown={(ev) => ev.preventDefault()}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  margin="normal"
                />
                <Button type="submit" variant="contained" fullWidth size="large" disabled={submitting} sx={{ mt: 3 }}>
                  {submitting ? "Setting up..." : "Claim my account"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ClaimEmailView;
